import React, { useState, useEffect } from 'react';
import { GridEditorActions } from '../../GridEditorPresenter';
import { Edit, X, Save, MapPin } from 'lucide-react';
import { GridElement } from '../../types';
import { synchronizeMeterNumber } from '../../utils/presenter.utils';
import { useNavigate, useParams } from 'react-router-dom';

interface ElementManagerProps {
  element: GridElement;
  actions: Pick<GridEditorActions, 'updateElement'>;
  isEditMode?: boolean;
}

export const ElementManager: React.FC<ElementManagerProps> = ({
  element,
  actions,
  isEditMode = false
}) => {
  const navigate = useNavigate();
  const params = useParams<{ planBTId: string }>();
  const planBTId = params.planBTId;
  const [showEditForm, setShowEditForm] = useState(false);
  const [showGpsEdit, setShowGpsEdit] = useState(false);
  const [gpsEditData, setGpsEditData] = useState({
    latitude: element.gpsCoordinates?.latitude ?? 0,
    longitude: element.gpsCoordinates?.longitude ?? 0,
  });
  const [editData, setEditData] = useState({
    name: element.name || '',
    number: element.number || '',
    status: element.status || 'ACTIVE',
    pole_type: element.properties?.pole_type || 'WOOD',
    power: element.properties?.power || 0,
    voltage: element.properties?.voltage || 0,
    current: element.properties?.current || 0,
    consumption: element.properties?.consumption || 0,
    capacity: element.properties?.capacity || 0,
    efficiency: element.properties?.efficiency || 0
  });

  // Réinitialiser les données quand l'élément change
  useEffect(() => {
    // Synchroniser l'élément avant de mettre à jour les données d'édition
    const synchronizedElement = synchronizeMeterNumber(element);
    
    setEditData({
      name: synchronizedElement.name || '',
      number: synchronizedElement.number || '',
      status: synchronizedElement.status || 'ACTIVE',
      pole_type: synchronizedElement.properties?.pole_type || 'WOOD',
      power: synchronizedElement.properties?.power || 0,
      voltage: synchronizedElement.properties?.voltage || 0,
      current: synchronizedElement.properties?.current || 0,
      consumption: synchronizedElement.properties?.consumption || 0,
      capacity: synchronizedElement.properties?.capacity || 0,
      efficiency: synchronizedElement.properties?.efficiency || 0
    });
    // Fermer les formulaires quand on change d'élément
    setShowEditForm(false);
    setShowGpsEdit(false);
    setGpsEditData({
      latitude: element.gpsCoordinates?.latitude ?? 0,
      longitude: element.gpsCoordinates?.longitude ?? 0,
    });
  }, [element]);

  const handleEditElement = () => {
    setShowEditForm(true);
  };

  const handleSaveElement = async () => {
    const name = editData.name?.trim() || '';
    if (!name) {
      alert('Le nom est obligatoire');
      return;
    }

    // Créer l'élément mis à jour
    const updatedElement: Partial<GridElement> = {
      name: name,
      number: editData.number,
      status: editData.status,
      properties: {
        pole_type: editData.pole_type,
        power: editData.power,
        voltage: editData.voltage,
        current: editData.current,
        consumption: editData.consumption,
        capacity: editData.capacity,
        efficiency: editData.efficiency
      }
    };

    // Synchroniser automatiquement les champs number et mtr_number pour les meters
    const synchronizedElement = synchronizeMeterNumber({
      ...element,
      ...updatedElement
    });

    // Mettre à jour l'élément dans l'état local avec les champs synchronisés
    actions.updateElement(element.id, synchronizedElement);
    
    console.log('✏️ Modification d\'un élément:', {
      elementId: element.id,
      name: name,
      status: editData.status,
      properties: editData
    });
    
    // Fermer le formulaire
    setShowEditForm(false);
    
    // Ne plus déclencher la sauvegarde automatique - juste marquer comme à mettre à jour
    console.log('✅ Élément marqué comme à mettre à jour. Utilisez le bouton "Sauvegarder" de la toolbar pour sauvegarder.');
  };

  const handleCancelEdit = () => {
    setEditData({
      name: element.name || '',
      number: element.number || '',
      status: element.status || 'ACTIVE',
      pole_type: element.properties?.pole_type || 'WOOD',
      power: element.properties?.power || 0,
      voltage: element.properties?.voltage || 0,
      current: element.properties?.current || 0,
      consumption: element.properties?.consumption || 0,
      capacity: element.properties?.capacity || 0,
      efficiency: element.properties?.efficiency || 0
    });
    setShowEditForm(false);
  };

  const handleSaveGps = () => {
    const lat = Number(gpsEditData.latitude);
    const lng = Number(gpsEditData.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    actions.updateElement(element.id, {
      gpsCoordinates: { latitude: lat, longitude: lng },
    });
    setShowGpsEdit(false);
  };

  const getElementIcon = (type: string) => {
    switch (type) {
      case 'pole': return '🏗️';
      case 'transformer': return '⚡';
      case 'switch': return '🔌';
      case 'meter': return '📊';
      case 'generator': return '🔋';
      case 'battery': return '🔋';
      case 'solar': return '☀️';
      default: return '🔧';
    }
  };

  const getElementTypeName = (type: string) => {
    switch (type) {
      case 'pole': return 'Poteau';
      case 'transformer': return 'Transformateur';
      case 'switch': return 'Interrupteur';
      case 'meter': return 'Compteur';
      case 'generator': return 'Générateur';
      case 'battery': return 'Batterie';
      case 'solar': return 'Panneau solaire';
      default: return 'Élément';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'Actif';
      case 'INACTIVE': return 'Inactif';
      case 'MAINTENANCE': return 'Maintenance';
      case 'OFFLINE': return 'Hors ligne';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-500';
      case 'INACTIVE': return 'bg-gray-400';
      case 'MAINTENANCE': return 'bg-yellow-500';
      case 'OFFLINE': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getPoleTypeLabel = (poleType: string) => {
    switch (poleType) {
      case 'WOOD': return 'Bois';
      case 'CONCRETE': return 'Béton';
      case 'STEEL': return 'Acier';
      default: return poleType;
    }
  };

  // Définir les champs pertinents selon le type d'élément
  const getElementFields = (type: string) => {
    const baseFields = ['name', 'number', 'status'];
    
    switch (type) {
      case 'pole':
        return [...baseFields, 'pole_type'];
      case 'transformer':
        return [...baseFields, 'power', 'voltage', 'current', 'capacity', 'efficiency'];
      case 'switch':
        return [...baseFields, 'voltage', 'current'];
      case 'meter':
        return [...baseFields];
      case 'generator':
        return [...baseFields, 'power', 'voltage', 'current', 'capacity', 'efficiency'];
      case 'battery':
        return [...baseFields, 'power', 'voltage', 'current', 'capacity', 'efficiency'];
      case 'solar':
        return [...baseFields, 'power', 'voltage', 'current', 'capacity', 'efficiency'];
      default:
        return baseFields;
    }
  };

  const getFieldLabel = (field: string) => {
    const labels: { [key: string]: string } = {
      name: 'Nom',
      number: 'Numéro',
      status: 'Statut',
      pole_type: 'Type de poteau',
      power: 'Puissance (kW)',
      voltage: 'Tension (V)',
      current: 'Courant (A)',
      consumption: 'Consommation (kWh)',
      capacity: 'Capacité (kW)',
      efficiency: 'Efficacité (%)'
    };
    return labels[field] || field;
  };

  const getFieldValue = (field: string) => {
    switch (field) {
      case 'name': return editData.name;
      case 'number': return editData.number;
      case 'status': return editData.status;
      case 'pole_type': return editData.pole_type;
      case 'power': return editData.power;
      case 'voltage': return editData.voltage;
      case 'current': return editData.current;
      case 'consumption': return editData.consumption;
      case 'capacity': return editData.capacity;
      case 'efficiency': return editData.efficiency;
      default: return '';
    }
  };

  const updateFieldValue = (field: string, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const renderFormField = (field: string) => {
    const label = getFieldLabel(field);
    const value = getFieldValue(field);

    if (field === 'status') {
      return (
        <div key={field}>
          <label className="block text-xs text-blue-800 mb-1">{label}</label>
          <select
            value={value}
            onChange={(e) => updateFieldValue(field, e.target.value)}
            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="ACTIVE">Actif</option>
            <option value="INACTIVE">Inactif</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="OFFLINE">Hors ligne</option>
          </select>
        </div>
      );
    }

    if (field === 'pole_type') {
      return (
        <div key={field}>
          <label className="block text-xs text-blue-800 mb-1">{label}</label>
          <select
            value={value}
            onChange={(e) => updateFieldValue(field, e.target.value)}
            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="WOOD">Bois</option>
            <option value="CONCRETE">Béton</option>
            <option value="STEEL">Acier</option>
          </select>
        </div>
      );
    }

    if (field === 'name') {
      return (
        <div key={field}>
          <label className="block text-xs text-blue-800 mb-1">{label} *</label>
          <input
            type="text"
            value={value}
            onChange={(e) => updateFieldValue(field, e.target.value)}
            placeholder="Nom de l'élément..."
            className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
      );
    }

    // Champs numériques
    return (
      <div key={field}>
        <label className="block text-xs text-blue-800 mb-1">{label}</label>
        <input
          type="number"
          value={value}
          onChange={(e) => updateFieldValue(field, parseFloat(e.target.value) || 0)}
          className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      </div>
    );
  };

  const renderDisplayField = (field: string) => {
    const label = getFieldLabel(field);
    const value = getFieldValue(field);

    if (field === 'status') {
      return (
        <div key={field}>
          <label className="block text-xs text-slate-600 mb-1">{label}</label>
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded">
              {getStatusLabel(value as string)}
            </div>
            <span className={`w-2 h-2 rounded-full ${getStatusColor(value as string)}`}></span>
          </div>
        </div>
      );
    }

    if (field === 'pole_type') {
      return (
        <div key={field}>
          <label className="block text-xs text-slate-600 mb-1">{label}</label>
          <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded">
            {getPoleTypeLabel(value as string)}
          </div>
        </div>
      );
    }

    if (field === 'name') {
      return (
        <div key={field}>
          <label className="block text-xs text-slate-600 mb-1">{label}</label>
          <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded">
            {value || 'Non défini'}
          </div>
        </div>
      );
    }

    // Champs numériques
    return (
      <div key={field}>
        <label className="block text-xs text-slate-600 mb-1">{label}</label>
        <div className="text-xs text-slate-800 px-2 py-1 bg-slate-100 rounded">
          {value || 'Non défini'}
        </div>
      </div>
    );
  };

  const relevantFields = getElementFields(element.type);

  return (
    <div className="border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-slate-900 flex items-center">
          <span className="text-sm mr-2">{getElementIcon(element.type)}</span>
          {getElementTypeName(element.type)} - {element.name || 'Sans nom'}
        </h4>
        {!showEditForm && isEditMode && (
          <button
            onClick={handleEditElement}
            className="text-xs px-2 py-1 h-6 border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1"
          >
            <Edit className="h-3 w-3" />
            Modifier
          </button>
        )}
      </div>

      {/* Formulaire de modification */}
      {showEditForm && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-medium text-blue-900">Modifier l'élément</h5>
            <button
              onClick={handleCancelEdit}
              className="h-5 w-5 p-0 rounded hover:bg-blue-100 flex items-center justify-center"
            >
              <X className="h-3 w-3 text-blue-600" />
            </button>
          </div>
          
          <div className="space-y-3">
            {relevantFields.map(field => renderFormField(field))}
            
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSaveElement}
                disabled={!editData.name?.trim()}
                className="text-xs px-3 py-1 h-6 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Save className="h-3 w-3" />
                Finir
              </button>
              <button
                onClick={handleCancelEdit}
                className="text-xs px-3 py-1 h-6 border border-blue-300 rounded hover:bg-blue-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Affichage en lecture seule */}
      {!showEditForm && (
        <div className="space-y-3">
          {/* Type d'élément */}
          <div className="bg-slate-50 p-3 rounded-lg border">
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Type d'élément
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm">{getElementIcon(element.type)}</span>
              <div className="text-xs text-slate-800 px-2 py-1 bg-white rounded">
                {getElementTypeName(element.type)}
              </div>
            </div>
          </div>

          {/* Champs pertinents selon le type */}
          <div className="bg-slate-50 p-3 rounded-lg border">
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Propriétés de l'élément
            </label>
            <div className="space-y-2">
              {relevantFields.map(field => renderDisplayField(field))}
            </div>
          </div>

          {/* Coordonnées GPS */}
          {element.gpsCoordinates && (
            <div className="bg-slate-50 p-3 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-slate-600">
                  📍 Coordonnées GPS
                </label>
                {!showGpsEdit && isEditMode && (
                  <button
                    onClick={() => {
                      setGpsEditData({
                        latitude: element.gpsCoordinates!.latitude,
                        longitude: element.gpsCoordinates!.longitude,
                      });
                      setShowGpsEdit(true);
                    }}
                    className="text-xs px-2 py-0.5 border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1"
                  >
                    <MapPin className="h-3 w-3" />
                    Modifier emplacement
                  </button>
                )}
                {showGpsEdit && (
                  <button
                    onClick={() => setShowGpsEdit(false)}
                    className="h-5 w-5 p-0 rounded hover:bg-slate-200 flex items-center justify-center"
                  >
                    <X className="h-3 w-3 text-slate-500" />
                  </button>
                )}
              </div>

              {showGpsEdit ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                      <input
                        type="number"
                        step="0.000001"
                        value={gpsEditData.latitude}
                        onChange={(e) => setGpsEditData(prev => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                      <input
                        type="number"
                        step="0.000001"
                        value={gpsEditData.longitude}
                        onChange={(e) => setGpsEditData(prev => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSaveGps}
                      className="text-xs px-3 py-1 h-6 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1"
                    >
                      <Save className="h-3 w-3" />
                      Confirmer
                    </button>
                    <button
                      onClick={() => setShowGpsEdit(false)}
                      className="text-xs px-3 py-1 h-6 border border-slate-300 rounded hover:bg-slate-100"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                    <div className="text-xs text-slate-800 px-2 py-1 bg-white rounded font-mono">
                      {element.gpsCoordinates.latitude.toFixed(6)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                    <div className="text-xs text-slate-800 px-2 py-1 bg-white rounded font-mono">
                      {element.gpsCoordinates.longitude.toFixed(6)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bouton "Voir le compteur" pour les meters */}
          {element.type === 'meter' && element.number && (
            <button
              onClick={() => {
                navigate(`/main/meters/${element.number}?from_plan=${planBTId}&device_name=${element.number}`, { replace: false });
              }}
              className="w-full px-4 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 shadow-sm"
            >
              Voir les détails du compteur
            </button>
          )}

        </div>
      )}
    </div>
  );
};
